// src/hooks/use-team.ts
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { teamKeys } from "@/lib/query";
import { decisionErrorText } from "@/lib/team/pto-decisions";
import { getJson, sendJson } from "@/lib/time/api-client";
import type {
  GuidanceRow,
  PendingApprovalsView,
  PersonEntry,
  PersonMonthsView,
  PtoBookResult,
  PtoDecisionInput,
  PtoDecisionResult,
  PtoRejectInput,
  PtoRequestsView,
  SignOffGridView,
  TeamMonthView,
  TimeOffView,
} from "@/schemas/team";

const monthQuery = (month: string) => `month=${encodeURIComponent(month)}`;
const personPath = (personId: string, tab: string, month: string) =>
  `/api/team/person/${encodeURIComponent(personId)}/${tab}?${monthQuery(month)}`;

export function useTeamMonth(month: string) {
  return useQuery({
    queryKey: teamKeys.month(month),
    queryFn: () => getJson<TeamMonthView>(`/api/team/month?${monthQuery(month)}`),
  });
}

export function usePersonMonths(personId: string | null, month: string) {
  return useQuery({
    queryKey: teamKeys.personMonths(personId ?? "", month),
    queryFn: () => getJson<PersonMonthsView>(personPath(personId ?? "", "months", month)),
    enabled: personId !== null,
  });
}

export function usePersonGuidance(personId: string | null, month: string, enabled: boolean) {
  return useQuery({
    queryKey: teamKeys.personGuidance(personId ?? "", month),
    queryFn: () => getJson<GuidanceRow[]>(personPath(personId ?? "", "guidance", month)),
    enabled: enabled && personId !== null,
  });
}

export function usePersonEntries(personId: string | null, month: string, enabled: boolean) {
  return useQuery({
    queryKey: teamKeys.personEntries(personId ?? "", month),
    queryFn: () => getJson<PersonEntry[]>(personPath(personId ?? "", "entries", month)),
    enabled: enabled && personId !== null,
  });
}

export function useTeamTimeOff(month: string) {
  return useQuery({
    queryKey: teamKeys.timeOff(month),
    queryFn: () => getJson<TimeOffView>(`/api/team/time-off?${monthQuery(month)}`),
  });
}

export function useTeamSignOffs(enabled: boolean) {
  return useQuery({
    queryKey: teamKeys.signOffs(),
    queryFn: () => getJson<SignOffGridView>("/api/team/signoffs"),
    enabled,
  });
}

export function useTeamPtoRequests() {
  return useQuery({
    queryKey: teamKeys.ptoRequests(),
    queryFn: () => getJson<PtoRequestsView>("/api/team/pto-requests"),
  });
}

const PENDING_REFETCH_MS = 60_000;

/**
 * Pending PTO requests the signed-in person can decide, for the nav badges and banners. Asked for
 * every signed-in user (the server answers 0 for non-approvers); refreshed on focus and each minute.
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: teamKeys.pendingApprovals(),
    queryFn: () => getJson<PendingApprovalsView>("/api/team/pending-approvals"),
    refetchOnWindowFocus: true,
    refetchInterval: PENDING_REFETCH_MS,
  });
}

/**
 * A decision moves the list, both calendars, the month totals, the badge, the sign-off grid and
 * the open person panel (its months carry PTO; booked days land in its time entries) together.
 */
export function invalidateDecision(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: teamKeys.ptoRequests() });
  void qc.invalidateQueries({ queryKey: ["team", "time-off"] });
  void qc.invalidateQueries({ queryKey: ["team", "month"] });
  void qc.invalidateQueries({ queryKey: teamKeys.pendingApprovals() });
  void qc.invalidateQueries({ queryKey: teamKeys.people() });
  // Sign-off TotalHours include booked PTO.
  void qc.invalidateQueries({ queryKey: teamKeys.signOffs() });
}

/** Decisions never auto-retry: a repeat could double-book or decide twice. */
function useTeamMutation<I, R>(url: string, success: (result: R) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => sendJson<R>("POST", url, input),
    retry: false,
    onSuccess: (result) => {
      toast.success(success(result));
      invalidateDecision(qc);
    },
    onError: (err: Error) => {
      toast.error(decisionErrorText(err));
      invalidateDecision(qc);
    },
  });
}

/** Approve result carries `bookFailed` and `message`; the screen offers `useBookPto` to retry. */
export function useApprovePto() {
  return useTeamMutation<PtoDecisionInput, PtoDecisionResult>("/api/team/pto/approve", (r) =>
    r.bookFailed ? (r.message ?? "Approved. Time not booked yet.") : "Approved",
  );
}

export function useBookPto() {
  return useTeamMutation<PtoDecisionInput, PtoBookResult>(
    "/api/team/pto/book",
    () => "Time booked",
  );
}

export function useRejectPto() {
  return useTeamMutation<PtoRejectInput, PtoDecisionResult>(
    "/api/team/pto/reject",
    () => "Rejected",
  );
}
